/**
 * Form schema + field descriptors for `OsCreateMblsClientDialog`.
 *
 * Extracted by LOCKER-410 (`DoD10`): the dialog was 376 lines before the
 * country-rejection state landed, and the ~400-line ceiling in CLAUDE.md is a
 * ceiling rather than a target. The field descriptors are plain data with no
 * state of their own, so they are the half that moves.
 */
import Joi from "joi"
import { FormField } from "./FormFields"

export const createMblsClientJoiSchema = Joi.object({
  lastName: Joi.string().required().min(1).max(1000).messages({
    "string.empty": "Last name is required",
  }),
  firstName: Joi.string().required().min(1).max(1000).messages({
    "string.empty": "First name is required",
  }),
  addressLabel: Joi.string().required().min(1).max(1000).messages({
    "string.empty": "Address label is required",
  }),
  appartNumber: Joi.string().optional().min(1).max(1000),
  addressNotes: Joi.string().optional().min(1).max(5000).allow(""),
  phoneLabel: Joi.string().required().min(1).max(1000),
  // phone number regex
  phoneNumber: Joi.string()
    .required()
    // .min(10)
    // .max(14)
    // .pattern(new RegExp(/^[+]*[(]{1,1}[0-9]{1,3}[)]{1,1}[-\s\./0-9]*$/))
    // enforce phone number to be in the format (123) 123-1234
    .pattern(
      new RegExp(
        /^[(]{1,1}[0-9]{3,3}[)]{1,1}[ ]{1,1}[0-9]{3,3}[-]{1,1}[0-9]{4,4}$/,
      ),
    )
    .message("Phone number must be valid format : (123) 123-1234"),
  notes: Joi.string().optional().min(1).max(5000).allow(""),
  addressArcgis: Joi.string().required().min(1).max(1000).messages({
    "string.empty": "Address is required",
  }),
  language: Joi.string().required().min(1).max(1000).valid("fr", "en"),
  email: Joi.string()
    .optional()
    .min(1)
    .max(1000)
    .email({ tlds: { allow: false } })
    .allow("")
    .messages({
      "string.email": "Email must be valid format",
    }),
}).or("phoneNumber", "email")

export type CreateMblsClientFieldsParams = {
  t: (key: string) => string
  /** ArcGIS suggestion lookup is in flight. */
  isSuggestionsLoading: boolean
  suggestions: Array<{ text: string; magicKey: string }>
  /** The raw text in the address box, mirrored for the controlled TextField. */
  localAddress?: string
  onAddressChange: (e: any) => void
  /**
   * Called on every autocomplete input change with the typed text and the
   * magic key of the suggestion it matches — `""` when it matches none.
   * LOCKER-410 `DoD3`: only a non-empty key may trigger a geocode.
   */
  onAddressInputChange: (value: string, magicKey: string) => void
  /**
   * Validation message to pin under the address box, or `undefined` for none.
   * LOCKER-410 `DoD11`: an address ArcGIS resolved outside `{CAN, USA}` is
   * rejected here, visibly, instead of being posted downstream half-formed.
   */
  addressError?: string
}

export const buildCreateMblsClientFields = ({
  t,
  isSuggestionsLoading,
  suggestions,
  localAddress,
  onAddressChange,
  onAddressInputChange,
  addressError,
}: CreateMblsClientFieldsParams): { fields0: FormField[]; fields1: FormField[] } => {
  const fields0: FormField[] = [
    {
      name: "lastName",
      label: t("lastName"),
      fieldType: "text",
      required: true,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "firstName",
      label: t("firstName"),
      fieldType: "text",
      required: true,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "addressLabel",
      label: t("type"),
      fieldType: "select",
      required: true,
      size: "small",
      fullWidth: true,
      options: [
        { id: "Home", label: t("home") },
        { id: "Work", label: t("work") },
        { id: "Other", label: t("other") },
      ],
      gridProps: { xs: 12, sm: 2 },
    },
    {
      name: "addressArcgis",
      label: t("address"),
      fieldType: "autocomplete",
      required: true,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 8 },
      autocompleteProps: {
        loading: isSuggestionsLoading,
        name: "addressArcgis",
        /**
         * Every suggestion the host returned, unfiltered.
         *
         * It used to be `localAddress ? suggestions.map(...) : []`, which is a
         * second, independent way for the list to come back empty: the
         * Autocomplete fires `onInputChange` with `""` on a programmatic reset
         * too, and that emptied `options` while `arc.suggestions` still held
         * the five addresses the operator had just seen fetched. The gate
         * bought nothing — `useArcgisSuggestions.refresh` already clears
         * `suggestions` for any query under three characters — so what the
         * host returned is now what the list shows.
         */
        options: suggestions.map((s) => ({
          id: s.text,
          label: s.text,
        })),
        autocompleteProps: {
          size: "small",
          /**
           * ArcGIS already did the matching, server-side and accent-aware.
           * MUI's default `filterOptions` then re-filtered those results with
           * a literal substring test against the raw typed text, so a
           * suggestion whose label did not *contain* what was typed was
           * silently dropped — typing `1200 françois` discarded all five of
           * `1200 Rue François …` because the label reads `Rue` between the
           * two words. The network tab showed five, the list showed
           * `noAddressFound`. Identity is the only correct filter for a
           * remote-search Autocomplete.
           */
          filterOptions: (opts: unknown[]) => opts,
          onInputChange(event: unknown, value: string) {
            onAddressInputChange(
              value,
              suggestions.find((s) => s.text === value)?.magicKey || "",
            )
          },
          noOptionsText: t("noAddressFound"),
          loadingText: t("loading"),
        },
        textFieldProps: {
          onChange: onAddressChange,
          value: localAddress,
          helperText: addressError,
          // `AutocompleteElement` renders its TextField with a hard
          // `error={!!error}` taken from react-hook-form's field state, AFTER
          // spreading `textFieldProps` — so a plain `error: true` here is
          // silently dropped and the message renders in ordinary grey, which
          // reads as a hint rather than a rejection. `FormHelperTextProps` and
          // `sx` are not on that override path, so the field turns red through
          // them instead. (The library's fallback for `helperText` is
          // `textFieldProps.helperText`, which is why the text itself does get
          // through.)
          FormHelperTextProps: { error: Boolean(addressError) },
          sx: addressError
            ? {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "error.main",
                },
                "& .MuiInputLabel-root": { color: "error.main" },
              }
            : undefined,
        },
      },
    },
    {
      name: "appartNumber",
      label: t("appt"),
      fieldType: "text",
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 2 },
    },
  ] as FormField[]

  const fields1: FormField[] = [
    {
      name: "addressNotes",
      label: t("addressNotes"),
      fieldType: "text",
      required: false,
      size: "small",
      fullWidth: true,
      InputProps: { multiline: true, rows: 2 },
      gridProps: { xs: 12, sm: 12 },
    },
    {
      name: "phoneLabel",
      label: t("type"),
      fieldType: "select",
      required: true,
      size: "small",
      fullWidth: true,
      options: [
        { id: "Mobile", label: t("mobile") },
        { id: "Home", label: t("home") },
        { id: "Work", label: t("work") },
        { id: "Other", label: t("other") },
      ],
      gridProps: { xs: 12, sm: 2 },
    },
    {
      name: "phoneNumber",
      label: t("phoneNumber"),
      fieldType: "text",
      required: true,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 10 },
      inputMask: "(999) 999-9999",
    },
    {
      name: "notes",
      label: t("notes"),
      fieldType: "text",
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 12 },
      InputProps: { multiline: true, rows: 5 },
    },
    {
      name: "email",
      label: t("email"),
      fieldType: "text",
      required: false,
      size: "small",
      fullWidth: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: "language",
      label: t("language"),
      fieldType: "select",
      required: true,
      size: "small",
      fullWidth: true,
      options: [
        { id: "fr", label: t("french") },
        { id: "en", label: t("english") },
      ],
      gridProps: { xs: 12, sm: 2 },
    },
  ] as FormField[]

  return { fields0, fields1 }
}
